/**
 * Modelo da página do setor: o que a tela mostra, calculado a partir das
 * demandas já combinadas (dados/combinar.js), das PEÇAS DESMEMBRADAS
 * (dados/pecas.js) e do estado do app (useEstadoMapa). Tudo puro — a página
 * só chama e desenha.
 *
 * MUDANÇA DA V3: o setor deixou de ser um código fixo e virou cadastro. Tudo
 * aqui passa a casar por `setorId` (o id do backend), não mais por "corte" /
 * "marcenaria". Quem tem o nome do setor é o catálogo (`setores.js`).
 *
 * Kanban do setor: a primeira coluna é a "Fila do setor" (o que o gestor
 * ainda não distribuiu) e as seguintes são os subsetores, cada uma com os
 * seus postos como grupos. Posto sem subsetor mora na fila.
 */
import { formatarDataLocal, plural } from "../../../utils/dinheiro";
import { startOfDay } from "../../../utils/status";
import { SITUACAO } from "../setores";
import { STATUS_AMBIENTE, STATUS_TAREFA, tarefaAberta, tarefaAtrasada } from "../dados/vocabulario";

export const COLUNA_FILA = "fila";
const SEM_POSTO = "sem-posto";
const MS_POR_DIA = 24 * 60 * 60 * 1000;

const porNome = (a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");
const porOrdem = (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0) || porNome(a, b);

/** Só o que está NESTE setor (as bandejas do mapa geral ficam fora). */
export function demandasDoSetor(combinadas, setorId) {
  if (!setorId) return [];
  return (combinadas || []).filter((d) => d.situacao === SITUACAO.SETOR && d.setorId === setorId);
}

export function tarefasDoSetor(tarefas, setorId) {
  if (!setorId) return [];
  return (tarefas || []).filter((t) => t.setor_id === setorId);
}

/** Peças combinadas (`dados/pecas.js`) que estão neste setor. */
export function pecasDoSetor(pecas, setorId) {
  if (!setorId) return [];
  return (pecas || []).filter((p) => p.setorId === setorId);
}

/** Colaboradores ativos ligados ao setor, por nome. */
export function colaboradoresDoSetor(colaboradores, setorId) {
  if (!setorId) return [];
  return (colaboradores || [])
    .filter((c) => c.ativo !== false && (c.setor_ids || []).includes(setorId))
    .sort(porNome);
}

export function subsetoresDoSetor(subsetores, setorId, { incluirInativos = false } = {}) {
  if (!setorId) return [];
  return (subsetores || [])
    .filter((s) => s.setor_id === setorId && (incluirInativos || s.ativo !== false))
    .sort(porOrdem);
}

export function postosDoSetor(postos, setorId, { incluirInativos = false } = {}) {
  if (!setorId) return [];
  return (postos || [])
    .filter((p) => p.setor_id === setorId && (incluirInativos || p.ativo !== false))
    .sort(porOrdem);
}

/** Números da faixa de KPIs do setor. */
export function resumoDoSetor({ demandas = [], tarefas = [], colaboradores = [], pecas = [] }, hoje = new Date()) {
  const conta = (lista, predicado) => lista.filter(predicado).length;
  return {
    ambientes: demandas.length,
    emProducao: conta(demandas, (d) => d.statusApp === STATUS_AMBIENTE.EM_PRODUCAO),
    pausados: conta(demandas, (d) => d.statusApp === STATUS_AMBIENTE.PAUSADO),
    atrasados: conta(demandas, (d) => d.atrasada),
    tarefasAbertas: conta(tarefas, tarefaAberta),
    tarefasAtrasadas: conta(tarefas, (t) => tarefaAtrasada(t, hoje)),
    pecas: pecas.length,
    pecasFora: conta(pecas, (p) => p.fora),
    pecasAtrasadas: conta(pecas, (p) => p.atrasada),
    colaboradores: colaboradores.length,
  };
}

// ------------------------------------------------------------------ kanban

/** Id do grupo (coluna x posto) onde o item cai. */
export function chaveDoLugar(subsetorId, postoId) {
  return `${subsetorId || COLUNA_FILA}|${postoId || SEM_POSTO}`;
}

export const lugarDaDemanda = (d) => ({ subsetorId: d.subsetorId || null, postoId: d.postoId || null });
export const lugarDaTarefa = (t) => ({ subsetorId: t.subsetor_id || null, postoId: t.posto_id || null });
/** A peça combinada já traz `subsetorId`/`postoId` em camelCase. */
export const lugarDaPeca = (p) => ({ subsetorId: p.subsetorId || null, postoId: p.postoId || null });

/** Subsetores e postos que ainda têm algo apontado (inativo continua visível). */
export function idsEmUso(demandas = [], tarefas = [], pecas = []) {
  const ids = new Set();
  [
    ...demandas.map(lugarDaDemanda),
    ...tarefas.filter(tarefaAberta).map(lugarDaTarefa),
    ...pecas.filter((p) => p.fora).map(lugarDaPeca),
  ].forEach((l) => {
    if (l.subsetorId) ids.add(l.subsetorId);
    if (l.postoId) ids.add(l.postoId);
  });
  return ids;
}

function grupoDoPosto(subsetorId, posto) {
  return {
    id: chaveDoLugar(subsetorId, posto.id),
    subsetorId,
    postoId: posto.id,
    titulo: posto.nome,
    posto,
    inativo: posto.ativo === false,
  };
}

function montarColuna(subsetor, postos) {
  const subsetorId = subsetor?.id || null;
  const doLugar = postos.filter((p) => (p.subsetor_id || null) === subsetorId);
  return {
    id: subsetorId || COLUNA_FILA,
    subsetorId,
    titulo: subsetor ? subsetor.nome : "Fila do setor",
    inativo: Boolean(subsetor && subsetor.ativo === false),
    grupos: [
      {
        id: chaveDoLugar(subsetorId, null),
        subsetorId,
        postoId: null,
        titulo: subsetor ? "Sem posto" : "Na fila",
        posto: null,
        inativo: false,
      },
      ...doLugar.map((p) => grupoDoPosto(subsetorId, p)),
    ],
  };
}

/**
 * Colunas do kanban do setor: [Fila do setor, ...subsetores por ordem], cada
 * uma com os grupos [sem posto, ...postos]. Subsetor ou posto desativado só
 * aparece enquanto algo ainda estiver nele (`emUso`), para nada sumir da tela.
 */
export function montarColunas(setorId, estado, emUso = new Set()) {
  const visivel = (item) => item.ativo !== false || emUso.has(item.id);
  const subsetores = subsetoresDoSetor(estado?.subsetores, setorId, { incluirInativos: true }).filter(visivel);
  const postos = postosDoSetor(estado?.postos, setorId, { incluirInativos: true }).filter(visivel);
  return [montarColuna(null, postos), ...subsetores.map((s) => montarColuna(s, postos))];
}

/**
 * Map<idDoGrupo, itens[]>. Item apontado para um lugar que a tela não
 * desenha (posto de outro subsetor, subsetor removido) cai no grupo "sem
 * posto" da coluna e, sem coluna, na fila — nunca some.
 */
export function distribuirNoKanban(colunas, itens, lugarDe) {
  const grupos = new Map();
  colunas.forEach((c) => c.grupos.forEach((g) => grupos.set(g.id, [])));
  (itens || []).forEach((item) => {
    const { subsetorId, postoId } = lugarDe(item);
    const candidatos = [chaveDoLugar(subsetorId, postoId), chaveDoLugar(subsetorId, null), chaveDoLugar(null, null)];
    const destino = candidatos.find((chave) => grupos.has(chave));
    grupos.get(destino).push(item);
  });
  return grupos;
}

const PESO_STATUS_AMBIENTE = { em_producao: 0, pausado: 1, aguardando: 2 };
const PESO_STATUS_TAREFA = { em_andamento: 0, pausada: 1, aguardando: 2, concluida: 3, cancelada: 4 };
// Peça: o que está circulando primeiro; o que já se encerrou vai para o fim.
const PESO_STATUS_PECA = { em_producao: 0, pausada: 1, aguardando: 2, retornada: 3, concluida: 4, cancelada: 5 };

function compararDatas(a, b) {
  if (a && b) return a - b;
  if (a) return -1;
  if (b) return 1;
  return 0;
}

/** Dentro do grupo: atrasados, depois em produção, pausados e fila; por prazo. */
export function ordenarDemandas(demandas) {
  return [...(demandas || [])].sort(
    (a, b) =>
      Number(b.atrasada) - Number(a.atrasada) ||
      (PESO_STATUS_AMBIENTE[a.statusApp] ?? 9) - (PESO_STATUS_AMBIENTE[b.statusApp] ?? 9) ||
      compararDatas(a.prazoEfetivo, b.prazoEfetivo) ||
      String(a.rotulo || "").localeCompare(String(b.rotulo || ""), "pt-BR", { numeric: true })
  );
}

/** Abertas primeiro (em andamento, pausadas, fila), encerradas no fim; por data limite. */
export function ordenarTarefas(tarefas) {
  const limite = (t) => (t.data_limite ? startOfDay(t.data_limite) : null);
  return [...(tarefas || [])].sort(
    (a, b) =>
      (PESO_STATUS_TAREFA[a.status] ?? 9) - (PESO_STATUS_TAREFA[b.status] ?? 9) ||
      compararDatas(limite(a), limite(b)) ||
      String(a.codigo || "").localeCompare(String(b.codigo || ""), "pt-BR", { numeric: true })
  );
}

/**
 * Peças no grupo: atrasadas na frente, depois pelo status (circulando antes
 * de encerrada), prazo e código. `combinarPecas` já ordena a lista inteira;
 * aqui é a ordem DENTRO do posto, que o kanban precisa refazer.
 */
export function ordenarPecas(pecas) {
  return [...(pecas || [])].sort(
    (a, b) =>
      Number(b.atrasada) - Number(a.atrasada) ||
      (PESO_STATUS_PECA[a.status] ?? 9) - (PESO_STATUS_PECA[b.status] ?? 9) ||
      compararDatas(a.prazoEfetivo, b.prazoEfetivo) ||
      String(a.codigo || "").localeCompare(String(b.codigo || ""), "pt-BR", { numeric: true })
  );
}

/**
 * Tarefas do /estado (abertas + encerradas nas últimas 24 h, sempre mais
 * novas) com o histórico de GET /tarefas (até 500): o do estado vence.
 */
export function mesclarPorId(historico, atuais) {
  const porId = new Map();
  (historico || []).forEach((t) => porId.set(t.id, t));
  (atuais || []).forEach((t) => porId.set(t.id, t));
  return [...porId.values()];
}

// ---------------------------------------------------------------- pessoas

function pessoasDoPosto(posto, indice) {
  return (posto?.colaborador_ids || []).map((id) => indice.colaboradores.get(id)).filter(Boolean);
}

/**
 * Quem responde pelo ambiente, sem repetir: colaboradores do posto, o da
 * tarefa aberta e o marceneiro da planilha (vinculado a um colaborador ou,
 * sem vínculo, pelo nome como está na planilha). Cada item:
 * {id, nome, origem: "posto"|"tarefa"|"planilha", vinculado}.
 */
export function pessoasDaDemanda(demanda, indice) {
  const vistas = new Set();
  const pessoas = [];
  const incluir = (colaborador, origem) => {
    if (!colaborador || vistas.has(colaborador.id)) return;
    vistas.add(colaborador.id);
    pessoas.push({ id: colaborador.id, nome: colaborador.nome, origem, vinculado: true });
  };
  pessoasDoPosto(indice.postos.get(demanda.postoId), indice).forEach((c) => incluir(c, "posto"));
  (demanda.tarefas || []).forEach((t) => incluir(indice.colaboradores.get(t.colaborador_id), "tarefa"));
  incluir(indice.colaboradores.get(demanda.colaboradorPlanilhaId), "planilha");
  const marceneiro = demanda.marceneiro;
  if (!demanda.colaboradorPlanilhaId && marceneiro && !marceneiro.semMarceneiro) {
    const nome = marceneiro.tipo ? `${marceneiro.nome} (${marceneiro.tipo})` : marceneiro.nome;
    pessoas.push({ id: `planilha:${nome}`, nome, origem: "planilha", vinculado: false });
  }
  return pessoas;
}

/**
 * Quem responde pela PEÇA: o colaborador apontado nela primeiro (é o
 * responsável escolhido), depois quem trabalha no posto onde ela está e quem
 * tem tarefa dela. Mesmo formato de `pessoasDaDemanda`.
 */
export function pessoasDaPeca(peca, indice) {
  const vistas = new Set();
  const pessoas = [];
  const incluir = (colaborador, origem) => {
    if (!colaborador || vistas.has(colaborador.id)) return;
    vistas.add(colaborador.id);
    pessoas.push({ id: colaborador.id, nome: colaborador.nome, origem, vinculado: true });
  };
  incluir(indice.colaboradores.get(peca.colaboradorId), "peca");
  pessoasDoPosto(indice.postos.get(peca.postoId), indice).forEach((c) => incluir(c, "posto"));
  (peca.tarefas || []).forEach((t) => incluir(indice.colaboradores.get(t.colaborador_id), "tarefa"));
  return pessoas;
}

/** "Naldo, Carlos" — texto curto dos responsáveis (tabela, busca). */
export function textoDosResponsaveis(demanda, indice) {
  return pessoasDaDemanda(demanda, indice).map((p) => p.nome).join(", ");
}

/** O mesmo texto, para a peça desmembrada. */
export function textoDosResponsaveisDaPeca(peca, indice) {
  return pessoasDaPeca(peca, indice).map((p) => p.nome).join(", ");
}

const ATIVIDADE_LIVRE = { estado: "livre", tom: "neutral", texto: "Sem atividade apontada", tarefa: null, demanda: null };

function tituloDaTarefa(tarefa) {
  return [tarefa.codigo, tarefa.titulo].filter(Boolean).join(" · ");
}

/**
 * O "balão" da pessoa na faixa da equipe — o que ela está fazendo agora:
 * 1) tarefa em andamento; 2) ambiente em produção no posto dela ou em que a
 * planilha a tem como marceneira; 3) tarefa pausada; 4) ambiente pausado
 * nesses mesmos lugares; senão, livre. É a mesma ordem da pessoa no mapa
 * geral (pessoas.js), para as duas telas não discordarem.
 * Devolve {estado: "trabalhando"|"pausado"|"livre", tom, texto, tarefa, demanda}.
 */
export function atividadeDoColaborador(colaborador, { tarefas = [], demandas = [], postos = [] }) {
  const minhas = tarefas.filter((t) => t.colaborador_id === colaborador.id);
  const emAndamento = minhas.find((t) => t.status === STATUS_TAREFA.EM_ANDAMENTO);
  if (emAndamento) {
    return { estado: "trabalhando", tom: "info", texto: tituloDaTarefa(emAndamento), tarefa: emAndamento, demanda: null };
  }
  const meusPostos = new Set(postos.filter((p) => (p.colaborador_ids || []).includes(colaborador.id)).map((p) => p.id));
  const meusAmbientes = demandas.filter(
    (d) => (d.postoId && meusPostos.has(d.postoId)) || d.colaboradorPlanilhaId === colaborador.id
  );
  const produzindo = meusAmbientes.find((d) => d.statusApp === STATUS_AMBIENTE.EM_PRODUCAO);
  if (produzindo) {
    return { estado: "trabalhando", tom: "info", texto: `Produzindo ${produzindo.rotulo}`, tarefa: null, demanda: produzindo };
  }
  const pausada = minhas.find((t) => t.status === STATUS_TAREFA.PAUSADA);
  if (pausada) {
    return { estado: "pausado", tom: "warning", texto: `Pausada: ${tituloDaTarefa(pausada)}`, tarefa: pausada, demanda: null };
  }
  const ambientePausado = meusAmbientes.find((d) => d.statusApp === STATUS_AMBIENTE.PAUSADO);
  if (ambientePausado) {
    return {
      estado: "pausado",
      tom: "warning",
      texto: `Pausado: ${ambientePausado.rotulo}`,
      tarefa: null,
      demanda: ambientePausado,
    };
  }
  const aguardando = minhas.filter((t) => t.status === STATUS_TAREFA.AGUARDANDO).length;
  if (aguardando) {
    return { ...ATIVIDADE_LIVRE, texto: `${plural(aguardando, "tarefa", "tarefas")} na fila` };
  }
  return ATIVIDADE_LIVRE;
}

// ------------------------------------------------------------------ textos

function diasEntre(inicio, fim) {
  const a = startOfDay(inicio);
  const b = startOfDay(fim);
  if (!a || !b) return 0;
  return Math.round((b - a) / MS_POR_DIA);
}

const diaCurto = (data) => formatarDataLocal(data).slice(0, 5);

/**
 * "Prazo 25/09", "Vence hoje", "Atrasado 3 dias · 19/09" ou "Sem prazo".
 * `data` já é Date local (planilha ou toDate do "YYYY-MM-DD" do app).
 */
export function textoDoPrazo(data, hoje = new Date()) {
  if (!data) return { texto: "Sem prazo", tom: "neutral" };
  const dias = diasEntre(hoje, data);
  if (dias < 0) return { texto: `Atrasado ${plural(-dias, "dia", "dias")} · ${diaCurto(data)}`, tom: "danger" };
  if (dias === 0) return { texto: "Vence hoje", tom: "warning" };
  return { texto: `Prazo ${diaCurto(data)}`, tom: "neutral" };
}

// A mesma régua vale para o PRAZO (masculino) e para a PRIORIDADE (feminino):
// concordar o texto evita o "Alta · definido na peça" que ninguém escreveria.
const ORIGEM_EM_TEXTO = {
  peca: ["definido na peça", "escolhida na peça"],
  ambiente: ["herdado do ambiente", "herdada do ambiente"],
  prazo: ["estimado pelo prazo", "estimada pelo prazo"],
  app: ["definido no Mapa", "definida no Mapa"],
  planilha: ["da planilha", "da planilha"],
};

/**
 * De onde vem o prazo (ou a prioridade), em texto — ADR-0059: o rótulo tem
 * que dizer a régua, senão o gestor não sabe em qual número acreditar.
 * `feminino` concorda o texto com "prioridade".
 */
export function textoDaOrigem(origem, { feminino = false } = {}) {
  const par = ORIGEM_EM_TEXTO[origem];
  return par ? par[feminino ? 1 : 0] : "";
}

/** "há 3 dias no setor" (ou null sem data de chegada). */
export function textoDoTempoNoSetor(demanda) {
  if (demanda.diasNoSetor == null) return null;
  if (demanda.diasNoSetor === 0) return "Chegou hoje";
  return `${plural(demanda.diasNoSetor, "dia", "dias")} no setor`;
}

/** "3 peças fora" (ou null quando nenhuma saiu do ambiente). */
export function textoDasPecasFora(quantas) {
  if (!quantas) return null;
  return plural(quantas, "peça fora", "peças fora");
}

/** Iniciais para o avatar: "Carlos Souza" vira "CS"; "Naldo" vira "NA". */
export function iniciais(nome) {
  const partes = String(nome || "").replace(/\(.*?\)/g, "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

export function primeiroNome(nome) {
  return String(nome || "").trim().split(/\s+/)[0] || "";
}
